import { useState } from 'react'
import { useAppStore } from '../../store/app'
import { sendPermissionResponse, sendPermissionPunt } from '../../api/ws'
import { sendPermissionResponse as sendPermissionResponseHTTP } from '../../api/client'
import { IdlePromptCard } from './IdlePromptCard'
import { PermissionRequestCard } from './PermissionRequestCard'
import type { PermissionDecision, PermissionDecisionBehavior } from '../../store/types'
import './PermissionOverlay.css'

// Single-ask overlay. Distinct from the inbox sheet (which is the
// triage view for working through a pile). This surface shows
// exactly ONE ask, set explicitly via singleAskRequestID, and only on
// explicit intent:
//
//   - Push tap with requestID: that exact ask is shown.
//   - A new ask arriving while you're on that agent's screen or in a chat
//     room (you're actively expecting it) — see the permission_request case
//     in api/ws.ts.
//
// Opening the app, coming back to it, or refreshing the browser never pops
// one: waiting approvals stay in the inbox behind the bell, answered in the
// list there (see refreshPendingApprovals in api/ws.ts).
//
// Dismiss flow (top-right X): clears singleAskRequestID without
// dequeueing the request. The pending stays in the queue and the
// banner still counts it; the user is just choosing "not right now."
//
// Respond flow (allow / deny / always_*): dequeue as usual and clear
// the overlay state.
//
// New asks arriving via WS while the user is in-app do NOT trigger
// this surface — banner + haptic only. The overlay is reserved for
// explicit deep-link and opens-after-being-away signals so the user
// isn't interrupted by every server-side change.
export function PermissionOverlay() {
  const askID = useAppStore((s) => s.singleAskRequestID)
  const current = useAppStore((s) =>
    askID ? s.pendingRequests.find((r) => r.id === askID) ?? null : null,
  )
  const dequeue = useAppStore((s) => s.dequeueRequest)
  const setSingleAskRequestID = useAppStore((s) => s.setSingleAskRequestID)
  // Punt (escalate) only makes sense when there's someone else to escalate to.
  // organizationMembers is loaded lazily, so treat >1 as "there may be another
  // approver" and stay silent otherwise — the server no-ops a punt on the final
  // rung anyway, this just avoids a misleading button in a solo household.
  const memberCount = useAppStore((s) => s.organizationMembers.length)
  const [sendError, setSendError] = useState(false)

  if (!current) return null

  function clearOverlay() {
    setSingleAskRequestID(null)
  }

  // Dequeue and slide in the next ask (or clear). "I'm working with this agent"
  // mode: if the user is still on the same agent's detail screen and another
  // pending ask exists for it, pop that one so they don't bounce out to the
  // banner. Asks for OTHER agents are left for banner triage. Runs only after a
  // response has actually been sent — never on a failed/dropped send.
  function advance(reqID: string, aiAgentInstanceID?: string | null) {
    dequeue(reqID)
    const s = useAppStore.getState()
    const top = s.navStack[s.navStack.length - 1]
    if (
      top?.kind === 'agent_detail' &&
      aiAgentInstanceID &&
      top.agentInstanceID === aiAgentInstanceID
    ) {
      const next = s.pendingRequests.find(
        (r) => r.id !== reqID && r.ai_agent_instance_id === aiAgentInstanceID,
      )
      if (next) {
        setSingleAskRequestID(next.id)
        return
      }
    }
    clearOverlay()
  }

  async function respond(
    decision: PermissionDecision,
    extras?: { selectedOptionID?: string; freeText?: string },
  ) {
    if (!current) return
    const req = current
    setSendError(false)
    const args = {
      requestID: req.id,
      behavior: decision.behavior,
      expiresAt: decision.expires_at,
      interrupt: decision.interrupt,
      selectedOptionID: extras?.selectedOptionID,
      freeText: extras?.freeText,
    }
    // Primary path: the device WebSocket. If the socket isn't OPEN the frame is
    // dropped, so fall back to the HTTP POST /response — a fresh connection,
    // immune to a closed/half-open socket, with a real status code. Only
    // dequeue on a confirmed send; if BOTH paths fail, keep the ask on screen
    // and surface an error so a lost response (e.g. deny_stop) is never silently
    // dismissed. The heartbeat reconnects fast, so a retry lands quickly.
    const delivered = sendPermissionResponse(args)
    if (!delivered) {
      try {
        await sendPermissionResponseHTTP(args)
      } catch {
        setSendError(true)
        return
      }
    }
    advance(req.id, req.ai_agent_instance_id)
  }

  function shortcut(
    behavior: PermissionDecisionBehavior,
    extras?: { selectedOptionID?: string; freeText?: string },
  ) {
    void respond(
      { behavior, expires_at: null, interrupt: behavior === 'deny_stop' },
      extras,
    )
  }

  // Punt: "I can't decide this — escalate to another approver." Doesn't resolve
  // the request (no allow/deny); the server advances the escalation ladder to
  // add the next approvers. We clear the overlay (the ask stays in the queue,
  // now escalated) so the user moves on.
  function punt() {
    if (!current) return
    setSendError(false)
    if (sendPermissionPunt(current.id)) {
      clearOverlay()
    } else {
      setSendError(true)
    }
  }

  const tool = current.tool_name.toLowerCase()

  return (
    <div className="permission-overlay">
      <div className="permission-overlay__chrome">
        <button
          className="permission-overlay__dismiss"
          onClick={clearOverlay}
          aria-label="Dismiss (keep in queue)"
          title="Dismiss for now — stays in the pending queue"
        >
          ✕
        </button>
      </div>
      {sendError && (
        <div className="permission-overlay__send-error" role="alert">
          Couldn’t reach the server — your response wasn’t sent. Check your
          connection and try again.
        </div>
      )}
      {tool === 'idle_prompt' ? (
        <IdlePromptCard request={current} onDismiss={clearOverlay} />
      ) : (
        <PermissionRequestCard
          request={current}
          onShortcut={shortcut}
          onAlwaysAllow={(expires_at) =>
            void respond({ behavior: 'always_allow', expires_at, interrupt: false })
          }
          onAlwaysDeny={(expires_at) =>
            void respond({ behavior: 'always_deny', expires_at, interrupt: false })
          }
        />
      )}
      {memberCount > 1 && tool !== 'idle_prompt' && (
        <button
          className="permission-overlay__escalate"
          onClick={punt}
          title="Escalate to another approver without deciding"
        >
          Can’t decide — escalate to another approver
        </button>
      )}
    </div>
  )
}
